// T3: Tenant mobile hardware control.
//
// Covers Stitch v2 design `tenant_authorization_mobile_hardware_control`.
// 375 px-first surface for tenant_admin / tenant_manager to see and
// unlock doors from a phone. Reuses /devices + /hardware/doors/:id/unlock
// — no new backend.

import { useCallback, useEffect, useState, useRef } from 'react';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';

interface Door {
  id: string;
  name?: string | null;
  status?: 'online' | 'offline' | 'unknown';
  locked?: boolean;
}

interface ListResponse<T> {
  data: T[];
}

export function TenantMobileHardwareControlPage() {
  const { state } = useAuth();
  const [doors, setDoors] = useState<Door[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const isAllowed =
    state.status === 'authed' &&
    ['master_admin', 'tenant_admin', 'tenant_manager'].includes(state.user.role);

  const reload = useCallback(() => {
    setError(null);
    apiClient
      .get<ListResponse<Door>>('/api/v1/hardware/doors')
      .then((r) => setDoors(r.data))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load doors'));
  }, []);

  useEffect(() => {
    if (isAllowed) reload();
  }, [isAllowed, reload]);

  if (!isAllowed) {
    return (
      <p role="alert" className="text-error">
        <Icon name="lock" /> Tenant-admin or higher required.
      </p>
    );
  }

  if (doors === null) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  // UX-013: one key per door per unlock intent. A fresh key on every tap meant
  // a retry after a timeout was a second unlock command rather than the same
  // one, which on a door is a physical consequence.
  const unlockKeysRef = useRef<Map<string, string>>(new Map());

  async function unlock(door: Door) {
    setBusyId(door.id);
    setActionErr(null);
    let key = unlockKeysRef.current.get(door.id);
    if (!key) {
      key = newIdempotencyKey();
      unlockKeysRef.current.set(door.id, key);
    }
    try {
      await apiClient
        .withIdempotencyKey(key)
        .post(`/api/v1/hardware/doors/${door.id}/unlock`, { reason: 'mobile_operator' });
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : `Could not unlock ${door.name ?? door.id}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="max-w-md mx-auto space-y-4 py-4">
      <header>
        <h1 className="text-xl font-semibold">Hardware control</h1>
        <p className="text-sm text-on-surface-variant">
          Tap a door to unlock for 5 seconds. Mobile-first.
        </p>
      </header>

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}
      {actionErr ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {actionErr}
        </p>
      ) : null}

      {doors.length === 0 ? (
        <Card>
          <CardBody>
            <p className="py-3 text-on-surface-variant">No doors found.</p>
          </CardBody>
        </Card>
      ) : (
        <ul className="grid gap-3">
          {doors.map((d) => (
            <li key={d.id}>
              <Card>
                <CardBody>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{d.name ?? d.id.slice(0, 8)}</p>
                      <Badge tone={d.status === 'online' ? 'success' : 'danger'}>
                        {d.status ?? 'unknown'}
                      </Badge>
                    </div>
                    <Button
                      onClick={() => unlock(d)}
                      disabled={busyId === d.id || d.status === 'offline'}
                      className="h-12"
                    >
                      <Icon name="lock_open" />{' '}
                      {busyId === d.id ? 'Unlocking…' : 'Unlock'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
